import { useCallback } from "react";
import { useSelector } from "react-redux";

import PageTitle from "@/react/components/PageTitle";
import { DataTable, useDataTable } from "@/react/components/table/useDataTable";
import { CustomerCard } from "@/react/features/customer/card";
import { customerIdSelector } from "@/react/features/customer/customerSlice";
import PageContentContainer from "@/react/features/styled/PageContentContainer";
import PageLayout from "@/react/features/styled/PageLayout";
import { errorNotification } from "@/react/notifications";

import columnDefinition from "./columnDefinition";
import {
  handleCleanupResponse,
  useCleanupDidsMutation,
  useGetDidsQuery,
} from "./didCleanupApi";
import schema from "./schema";

const dataKey = schema.fields.uuid.spec.key;
const staticTableArgs = {
  title: "Did Clean Up",
  schema: { definition: schema, key: dataKey },
  columns: columnDefinition(),
  mutationsFilter: { delete: true },
};

const DidCleanupPage = () => {
  const customerId = useSelector(customerIdSelector);
  const getDidsQueryHookResult = useGetDidsQuery(customerId);
  const [cleanupDids, { isLoading: isCleaningUp }] = useCleanupDidsMutation();

  const { props, finalMutations } = useDataTable({
    queryHookResult: getDidsQueryHookResult,
    isLoadingFn: (isFetching) => isFetching || isCleaningUp,
    ...staticTableArgs,
  });

  const { rowsToDelete, setRowsToDelete, setData } = finalMutations;
  const onSave = useCallback(() => {
    if (rowsToDelete.size > 0) {
      const payload = {
        uuids: Array.from(rowsToDelete.keys()),
        customerId,
      };

      cleanupDids(payload)
        .unwrap()
        .then(
          handleCleanupResponse({
            rowsToDelete,
            setRowsToDelete,
            setData,
            dataKey,
          })
        )
        .catch(errorNotification);
    }
  }, [rowsToDelete, setRowsToDelete, setData, cleanupDids]);

  return (
    <PageLayout>
      <CustomerCard hasPbxSelector={true} />
      <PageContentContainer>
        <PageTitle>DID Cleanup</PageTitle>
        <DataTable onSave={onSave} {...props} />
      </PageContentContainer>
    </PageLayout>
  );
};

export default DidCleanupPage;
